import {
  TranscriptElement,
  TranscriptTextElement,
  RelativeTranscriptElement,
} from '../../types.ts/transcript';

export class TranscriptSegment {
  private words: TranscriptElement[];
  private wordsWithPunctuation: TranscriptElement[];
  private startTime: number; // OriginalTranscript anchored start time.
  private endTime: number; // OriginalTranscript anchored end time.
  private timelineStartTime?: number; // VideoElement parent's start time.

  private _precedingWord: TranscriptTextElement | null = null;
  private _followingWord: TranscriptTextElement | null = null;

  /**
   * Constructs a TranscriptSegment from the original transcription, filtered by time range
   * @param originalTranscription The full array of transcript elements
   * @param startTime Media start time for this segment
   * @param endTime Media end time for this segment
   */
  constructor(
    originalTranscription: TranscriptElement[],
    startTime: number,
    endTime: number,
    timelineStartTime?: number, // Starting position of segment on the timeline
  ) {
    this.startTime = startTime;
    this.endTime = endTime;
    this.timelineStartTime = timelineStartTime;

    // Filter elements based on time range
    this.words = this.filterElements(originalTranscription, startTime, endTime);

    // Compute and cache the words with leading and trailing punctuation
    this.wordsWithPunctuation = this.computeElementsWithPunctuation(
      originalTranscription,
    );
  }

  get start(): number {
    return this.startTime;
  }

  get end(): number {
    return this.endTime;
  }

  get firstWord(): TranscriptElement | null {
    return this.words.length > 0 ? this.words[0] : null;
  }

  get lastWord(): TranscriptElement | null {
    return this.words.length > 0 ? this.words[this.words.length - 1] : null;
  }

  get precedingWord(): TranscriptTextElement | null {
    return this._precedingWord;
  }

  get followingWord(): TranscriptTextElement | null {
    return this._followingWord;
  }

  /**
   * Checks if the segment contains the supplied transcript element
   * base on its start and end times
   * @param element The transcript element to check
   * @returns True if the element is contained within the segment
   */
  contains(element: TranscriptTextElement): boolean {
    // Check if this element's time range is covered by the segment's time range
    const timeRangeContained =
      element.ts >= this.startTime && element.end_ts <= this.endTime;

    // Check if it's already in our words array (direct object reference)
    const wordAlreadyIncluded = this.words.includes(element);

    // Also check by word_index if not directly included
    const wordIndexMatch =
      !wordAlreadyIncluded &&
      this.words.some(
        (word) =>
          word.type === 'text' &&
          (word as TranscriptTextElement).word_index === element.word_index,
      );

    const result =
      timeRangeContained && (wordAlreadyIncluded || wordIndexMatch);

    return result;
  }

  /**
   * Checks if word_index is already in the segment
   * @param wordIndex The word_index to check
   * @returns True if the word_index is already in the segment
   */
  containsWordIndex(wordIndex: number): boolean {
    return this.words.some(
      (word) =>
        word.type === 'text' &&
        (word as TranscriptTextElement).word_index === wordIndex,
    );
  }

  /**
   * Finds the closest text element before the given time
   * @param elements Array of transcript elements
   * @param time Time to search before
   * @returns The closest text element before the time or null if none found
   */
  private findPrecedingTextElement(
    elements: TranscriptElement[],
    time: number,
  ): TranscriptTextElement | null {
    const textElements = elements.filter(
      (el) => el.type === 'text',
    ) as TranscriptTextElement[];

    // Find the last text element that starts before our segment starts
    return (
      textElements
        .filter((el) => el.ts < time)
        .sort((a, b) => b.ts - a.ts)[0] || null
    );
  }

  /**
   * Finds the closest text element after the given time
   * @param elements Array of transcript elements
   * @param time Time to search after
   * @returns The closest text element after the time or null if none found
   */
  private findFollowingTextElement(
    elements: TranscriptElement[],
    time: number,
  ): TranscriptTextElement | null {
    const textElements = elements.filter(
      (el) => el.type === 'text',
    ) as TranscriptTextElement[];

    // Find the first text element that starts at or after our segment ends
    return (
      textElements
        .filter((el) => el.ts >= time)
        .sort((a, b) => a.ts - b.ts)[0] || null
    );
  }

  /**
   * Filters transcript elements based on the time range, including relevant punctuation
   * @param originalTranscription The full array of transcript elements
   * @param startTime The start time of the segment to build
   * @param endTime The end time of the segment to build
   * @returns An array of filtered transcript elements present between the start and end times
   */
  private filterElements(
    originalTranscription: TranscriptElement[],
    startTime: number,
    endTime: number,
  ): TranscriptElement[] {
    const textElements = originalTranscription.filter(
      (el) => el.type === 'text',
    ) as TranscriptTextElement[];

    // Find the first and last text elements within the time range
    const startIdx = textElements.findIndex(
      (el) => el.ts - (el.buffer_before_ts || 0) >= startTime,
    );
    if (startIdx === -1) return []; // No text elements start after startTime

    const endIdx = textElements.findIndex((el) => el.end_ts > endTime);
    const lastIncludedIdx =
      endIdx === -1 ? textElements.length - 1 : endIdx - 1;
    if (lastIncludedIdx < startIdx) return []; // No elements within range

    // Map back to indices in the original transcription
    const startElementIdx = originalTranscription.indexOf(
      textElements[startIdx],
    );
    const endElementIdx = originalTranscription.indexOf(
      textElements[lastIncludedIdx],
    );

    // Find the preceding and following words (text elements only)
    this._precedingWord = this.findPrecedingTextElement(
      originalTranscription,
      startTime,
    );
    this._followingWord = this.findFollowingTextElement(
      originalTranscription,
      endTime,
    );

    // Include elements up to and including the punctuation after the last text, if present
    let includeUpTo = endElementIdx;
    if (
      endElementIdx + 1 < originalTranscription.length &&
      originalTranscription[endElementIdx + 1].type === 'punct'
    ) {
      includeUpTo = endElementIdx + 1;
    }

    return originalTranscription.slice(startElementIdx, includeUpTo + 1);
  }

  /**
   * Gets the concatenated text of the segment, including punctuation
   * @returns The text string (e.g., "Hello world this ")
   */
  getText(): string {
    return this.words.map((w) => w.value).join('');
  }

  /**
   * Gets the words with timestamps, adjusted relative to the element's start time
   * Used by TranscriptRemoveCommand to determine which words to remove
   * @returns Array of RelativeTranscriptElement with relative timestamps
   */
  getWordsWithTimestamps(): RelativeTranscriptElement[] {
    return this.words
      .filter((el) => el.type === 'text')
      .map((el) => {
        const textEl = el as TranscriptTextElement;
        const relativeStartTime =
          textEl.ts - this.startTime - (textEl.buffer_before_ts ?? 0);
        const relativeEndTime =
          textEl.end_ts - this.startTime + (textEl.buffer_after_ts ?? 0);
        const timelineStartTime =
          (this.timelineStartTime ?? 0) + (relativeStartTime ?? 0);
        const timelineEndTime =
          (this.timelineStartTime ?? 0) + (relativeEndTime ?? 0);

        return {
          ...textEl,
          relative_ts: relativeStartTime,
          relative_end_ts: relativeEndTime,
          timeline_ts: timelineStartTime,
          timeline_end_ts: timelineEndTime,
          word_index: textEl.word_index,
        } as RelativeTranscriptElement;
      });
  }

  /**
   * Gets all transcript elements (including punctuation) in the segment
   * @returns Array of all TranscriptElements
   */
  getWords(): TranscriptElement[] {
    return this.words;
  }

  /**
   * Computes all transcript elements in this segment with both leading and trailing punctuation
   * during initialization
   *
   * @param originalTranscript The full original transcript array
   * @returns All segment words plus leading and trailing punctuation
   * @private Used internally during construction
   */
  private computeElementsWithPunctuation(
    originalTranscript: TranscriptElement[],
  ): TranscriptElement[] {
    // Start with the existing words that are already in our time range
    if (this.words.length === 0) {
      return [];
    }

    // Find the first and last word in our segment
    const firstWord = this.words[0];
    const lastWord = this.words[this.words.length - 1];

    if (!firstWord || !lastWord) {
      return this.words;
    }

    // Find elements' positions in the original transcript
    const firstWordIdx = originalTranscript.indexOf(firstWord);
    const lastWordIdx = originalTranscript.indexOf(lastWord);

    if (firstWordIdx === -1 || lastWordIdx === -1) {
      // Elements not found, just return what we have
      return this.words;
    }

    // ---- Find leading punctuation ----
    let leadingStartIdx = firstWordIdx;

    // Look backward, adding punctuation until we hit the previous text element
    for (let i = firstWordIdx - 1; i >= 0; i--) {
      const el = originalTranscript[i];

      if (el.type === 'punct') {
        // Include all punctuation
        leadingStartIdx = i;
      } else if (el.type === 'text') {
        // Stop when we hit a text element
        break;
      }
    }

    // ---- Find trailing punctuation ----
    let trailingEndIdx = lastWordIdx;

    // Look ahead, adding punctuation until we hit the next text element
    for (let i = lastWordIdx + 1; i < originalTranscript.length; i++) {
      const el = originalTranscript[i];

      if (el.type === 'punct') {
        // Include all punctuation
        trailingEndIdx = i;
      } else if (el.type === 'text') {
        // Stop when we hit a text element
        break;
      }
    }

    // Combine leading punctuation + words + trailing punctuation
    let result: TranscriptElement[] = [];

    // Add leading punctuation if found
    if (leadingStartIdx < firstWordIdx) {
      result = result.concat(
        originalTranscript.slice(leadingStartIdx, firstWordIdx),
      );
    }

    // Add our words
    result = result.concat(this.words);

    // Add trailing punctuation if found
    if (trailingEndIdx > lastWordIdx) {
      result = result.concat(
        originalTranscript.slice(lastWordIdx + 1, trailingEndIdx + 1),
      );
    }

    return result;
  }

  /**
   * Gets all transcript elements in this segment with both leading and trailing punctuation
   *
   * Returns all words in the segment plus any:
   * - Leading punctuation before the first word (up to the previous text element)
   * - Trailing punctuation after the last word (up to the next text element)
   *
   * @returns All segment words plus surrounding punctuation
   */
  getElementsWithPunctuation(): TranscriptElement[] {
    return this.wordsWithPunctuation;
  }
}
